import { MutableRecordStore, Type } from '@deqo/foundation';
import { injectable } from 'inversify';
import { DoubleMap } from '../../CodesForFoundation';
import { BoardLiteData, EntityWithLabel, EntityWithName, UserLiteData, WorkspaceData, WorkspaceFolderData } from '../../api/ApiTypes';
import { WriteStore } from '../../concepts/revive/WriteStore';

const boardTemplate = {
    id: '',
    workspaceId: null as string,
    folderId: null as string,
    label: '',
    type: null as number,
    thumbUrl: null as string,
    special: false,
    pinned: false,
    path: '',
    createdAt: new Date(1970),
    updatedAt: new Date(1970),
};

export class BoardModel extends MutableRecordStore(boardTemplate) {
    public static create(_broker: ModelBroker, data: BoardLiteData) {
        const m = new BoardModel();
        m.setState({
            id: data.id,
            workspaceId: data.workspaceId,
            folderId: data.folderId,
            label: data.label,
            type: data.type,
            thumbUrl: data.thumbUrl,
            special: data.special || false,
            pinned: data.pinned,
            path: data.url || `~/${data.id}`,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
        });
        return m;
    }
}

const folderTemplate = {
    id: '',
    workspaceId: '',
    label: '',
    icon: '',
    boards: [] as BoardModel[],
    path: '',
    special: false,
    createdAt: new Date(1970),
    updatedAt: new Date(1970),
};

export class FolderModel extends MutableRecordStore(folderTemplate) {
    public static create(broker: ModelBroker, data: WorkspaceFolderData) {
        const m = new FolderModel();
        m.updateState({
            id: data.id,
            workspaceId: data.workspaceId,
            label: data.label,
            icon: data.icon,
            boards: (data.boards ?? []).map(x => broker.allocate(BoardModel, x)),
            path: data.path || `~/${data.id}`,
            special: data.special || false,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
        });
        return m;
    }
}

const userTemplate = {
    id: '',
    name: '',
    email: '',
    photo: '',
    color: '',
    createdAt: new Date(1970),
};

export class UserModel extends MutableRecordStore(userTemplate) {
    public static create(broker: ModelBroker, data: UserLiteData) {
        const m = new UserModel();
        m.setState({
            id: data.id,
            name: data.name,
            email: data.email,
            photo: data.photo,
            color: data.color,
            createdAt: data.createdAt,
        });
        return m;
    }
}

const workspaceTemplate = {
    id: '',
    accountId: '',
    alias: '',
    type: '',
    label: '',
    description: '',
    joined: false,
    boards: [] as BoardModel[],
    folders: [] as FolderModel[],
    members: [] as UserModel[],
    public: false,
    path: '',
    createdAt: new Date(1970),
    updatedAt: new Date(1970),
};

const selectAlias = (ws: { id: string, type: string }) => {
    if (ws.type === 'library') return 'library';
    return ws.id;
};

export class WorkspaceModel extends MutableRecordStore(workspaceTemplate) {

    public static createNull(accountId: string) {
        const m = new WorkspaceModel();
        m.setState({
            id: 'WO0000000000',
            accountId,
            alias: null,
            type: 'null',
            label: '',
            description: '',
            joined: false,
            boards: [],
            folders: [],
            members: [],
            public: false,
            path: `~/${accountId}`,
            createdAt: new Date(2019),
            updatedAt: new Date(2019),
        });
        return m;
    }

    public static create(broker: ModelBroker, data: WorkspaceData) {
        const m = new WorkspaceModel();
        m.setState({
            id: data.id,
            accountId: data.accountId,
            alias: selectAlias(data),
            type: data.type,
            // label: data.type === 'user' ? 'Private Boards' : data.label,
            label: data.label,
            description: data.description,
            joined: false,
            boards: data.boards.map(x => broker.allocate(BoardModel, x)),
            folders: data.folders.map(x => broker.allocate(FolderModel, x)),
            members: [],
            public: data.public,
            path: data.path || `~/${data.id}`,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
        });
        return m;
    }

    public findBoard(id: string) {
        return this.boards.find(x => x.id === id) ??
            this.folders.flatMap(x => x.boards).find(x => x.id === id);
    }

    public findFolder(id: string) {
        return this.folders.find(x => x.id === id);
    }
}

export abstract class ListModel<T> extends WriteStore<T[]> {

    protected state: T[] = [];

    public find: T[]['find'] = (p, t) => {
        return this.state.find(p, t);
    };
}

type Model<T = string> = { readonly id: T };
type ModelLike = Model<string>;

type SingletonModelType<M extends Model<'$'>> = Type<M> & {
    create(broker: ModelBroker): M,
};

type RegularModelType<M extends Model = any, D extends ModelLike = any> = Type<M> & {
    create(broker: ModelBroker, data: D): M,
};

@injectable()
export class ModelBroker {

    private register = new DoubleMap<Type<any>, string, Model>();

    constructor(private readonly parent?: ModelBroker) {
    }

    public allocate<M extends Model<'$'>>(type: SingletonModelType<M>): M;
    public allocate<M extends Model, D extends ModelLike>(type: RegularModelType<M, D>, data: D): M;
    public allocate(type: any, data?: any): any {
        const id = !!data ? data.id : '$';
        return this.get(type, id) ?? this.createInstance(type, data);
    }

    public release(model: Model): void {
        this.register.delete((model as any).constructor, model.id);
        this.parent?.release(model);
    }

    public get<M extends Model<'$'>>(type: SingletonModelType<M>): M;
    public get<M extends Model, D extends ModelLike>(type: RegularModelType<M, D>, id: string): M;
    public get(type: any, id?: string): any {
        id = id ?? '$';
        return (
            this.parent?.get(type, id) ??
            this.register.get(type, id)
        );
    }

    public has<M extends Model<'$'>>(type: SingletonModelType<M>): boolean;
    public has<M extends Model, D extends ModelLike>(type: RegularModelType<M, D>, id: string): boolean;
    public has(type: any, id?: string): any {
        return !!this.get(type, id);
    }

    private createInstance(type: any, data?: any): any {
        const instance = !!data ? type.create(this, data) : type.create(this);
        this.register.set(type, instance.id, instance);
        return instance;
    }
}

export const nameSorter = (a: EntityWithName, b: EntityWithName) => a.name.localeCompare(b.name);
export const labelSorter = (a: EntityWithLabel, b: EntityWithLabel) => a.label.localeCompare(b.label);
